import * as React from "react";
import {
	Paragraph,
	InlineText,
	Icon,
	IconError,
	Tooltip,
	TooltipProps,
	useUniqueId,
	cn,
} from "@siteimprove/fancylib";
import { DataObserveKey, VisualComponent } from "@siteimprove/fancylib";
import { FormControl } from "../form/form";
import * as scss from "./form-element-wrapper.scss";

type FormControlFunctionalChild = {
	name: string;
	id: string;
	"aria-labelledby": string;
	"aria-describedby"?: string;
};

type BaseElementWrapperProps = {
	/** Label for the form control */
	label: string;
	/** Form control */
	children: React.ReactElement<FormControl<unknown>> | React.FC<FormControlFunctionalChild>;
	/** Value for the name attribute */
	name: string;
	/** The content of the form control tooltip */
	tooltip?: string | React.ReactElement<TooltipProps>;
	/** Invalid state of the form control */
	invalid?: boolean;
	/** Error message to be displayed when form control is in an invalid state */
	error?: React.ReactNode;
	/** Helptext to understand the context of the form control better */
	helptext?: string | React.ReactNode;
	/** Positions the helptext above or below the form control (defaults to "below") */
	helptextPosition?: "above" | "below";
	/** When true label and form control are displayed horizontally next to each other */
	horizontal?: boolean;
	/** If another component (e.g. a button) is used together with a regular form control as a group */
	isGroup?: boolean;
} & VisualComponent &
	DataObserveKey;

export const FormElementWrapper = (props: BaseElementWrapperProps): JSX.Element => {
	const {
		label,
		children,
		name,
		tooltip,
		helptext,
		helptextPosition = "below",
		horizontal,
		invalid,
		error,
		isGroup,
		className,
		style,
	} = props;
	const controlId = useUniqueId("control");
	const labelId = useUniqueId("label");
	const helptextId = useUniqueId("helptext");
	const errorId = useUniqueId("error");
	const isInErrorState = invalid && Boolean(error);

	const helpSection = (p: "above" | "below") =>
		helptext &&
		p === helptextPosition && (
			<div className={scss[p]}>
				<Paragraph id={helptextId} size="xSmall" tone="subtle">
					{helptext}
				</Paragraph>
			</div>
		);

	const renderTooltip = () =>
		typeof tooltip === "string" ? (
			<Tooltip variant={{ type: "icon-only" }} content={tooltip}>
				<Icon size={Tooltip.iconSize}>{Tooltip.icon}</Icon>
			</Tooltip>
		) : (
			tooltip
		);

	const renderContent = (): React.ReactNode => {
		const enhancedProps = {
			name: name,
			id: controlId,
			"aria-labelledby": labelId,
			"aria-describedby": cn(isInErrorState && errorId, !!helptext && helptextId),
		};

		// function like children
		if (typeof children === "function") {
			return children(enhancedProps);
		}

		return React.cloneElement(children, {
			...enhancedProps,
			invalid: children.props.invalid !== undefined ? children.props.invalid : invalid, //favor child prop if it is set
		});
	};

	return (
		<div
			data-observe-key={props["data-observe-key"]}
			data-component="form-element-wrapper"
			className={cn(className, scss.formElement, horizontal && scss.horizontal)}
			style={style}
			role={isGroup ? "group" : undefined}
			aria-labelledby={isGroup ? labelId : undefined}
		>
			<InlineText
				size="small"
				emphasis="strong"
				lineHeight="multi-line"
				className={cn(scss.formLabel, horizontal && scss.horizontal)}
			>
				<label id={labelId} htmlFor={controlId}>
					{label}
				</label>
				{tooltip && renderTooltip()}
			</InlineText>
			<div className={cn(scss.formContent, horizontal && scss.horizontal)}>
				{helpSection("above")}
				{renderContent()}
				<div aria-live="polite" aria-atomic="true">
					{isInErrorState && (
						<div className={scss.error}>
							<Icon size="medium">
								<IconError className={scss.errorIcon} />
							</Icon>
							<Paragraph id={errorId} size="xSmall" tone="negative">
								{error}
							</Paragraph>
						</div>
					)}
				</div>
				{helpSection("below")}
			</div>
		</div>
	);
};
